import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import { LEGACY_SLUGS, relatedArticles } from "@/data/blogArticles";
import { BucketGrid } from "@/components/BlogSection";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

export default function BlogArticlePage({ article }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const renderRich = (text) =>
    String(text)
      .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g)
      .map((part, i) => {
        const bold = part.match(/^\*\*([^*]+)\*\*$/);
        if (bold)
          return (
            <strong key={i} className="font-bold text-slate-900">
              {bold[1]}
            </strong>
          );
        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link)
          return (
            <Link
              key={i}
              to={link[2]}
              className="font-semibold text-flame underline decoration-flame/40 underline-offset-4 transition-colors hover:decoration-flame"
            >
              {link[1]}
            </Link>
          );
        return part;
      });

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    } else {
      scrollToId("book");
    }
  };

  const pagePath = LEGACY_SLUGS.has(article.slug)
    ? `/${article.slug}`
    : `/blog/${article.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: { "@type": "Person", name: "Todd Meisler" },
    publisher: { "@type": "Organization", name: "ZD Design" },
    mainEntityOfPage: `https://www.zddesignagency.com${pagePath}`,
    url: `https://www.zddesignagency.com${pagePath}`,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.zddesignagency.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.zddesignagency.com/blog" },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://www.zddesignagency.com${pagePath}` },
    ],
  };

  return (
    <main data-testid="blog-article-page">
      <Seo
        title={`${article.title} — ZD Design`}
        description={article.description}
        path={pagePath}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <section className="grain relative overflow-hidden pb-12 pt-40 md:pb-14 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Link
              to="/blog"
              data-testid="blog-article-back-link"
              className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to the blog
            </Link>
            <Eyebrow>
              {article.category} · {article.readTime}
            </Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.0] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              {article.title}
            </h1>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <img
              src={article.graphic}
              alt={`${article.category} illustration`}
              data-testid="blog-article-graphic"
              className="w-full rounded-2xl border border-slate-200 bg-white"
            />
          </FadeUp>
          <div data-testid="blog-article-body" className="mt-10">
            {article.content.map((b, i) => {
              if (b.type === "h2")
                return (
                  <h2
                    key={i}
                    className="mt-12 font-display text-2xl font-extrabold tracking-tight text-slate-900 first:mt-0 sm:text-3xl"
                  >
                    {b.text}
                  </h2>
                );
              if (b.type === "ul")
                return (
                  <ul key={i} className="mt-5 space-y-3">
                    {b.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 text-lg leading-relaxed text-mist"
                      >
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
                        <span>{renderRich(item)}</span>
                      </li>
                    ))}
                  </ul>
                );
              if (b.type === "bottom")
                return (
                  <div
                    key={i}
                    data-testid="blog-article-bottom-line"
                    className="glass mt-12 rounded-2xl border border-flame/30 p-7"
                  >
                    <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                      The bottom line
                    </div>
                    <p className="mt-3 text-lg leading-relaxed text-slate-800">
                      {renderRich(b.text)}
                    </p>
                  </div>
                );
              if (b.type === "img")
                return (
                  <img
                    key={i}
                    src={b.src}
                    alt={b.alt}
                    data-testid="blog-article-inline-img"
                    className="mt-10 w-full rounded-2xl border border-slate-200 bg-white"
                  />
                );
              return (
                <p
                  key={i}
                  className="mt-5 text-lg leading-relaxed text-mist first:mt-0"
                >
                  {renderRich(b.text)}
                </p>
              );
            })}
          </div>
          {article.links && article.links.length > 0 && (
            <div
              data-testid="blog-article-related"
              className="glass mt-8 rounded-2xl border border-slate-200 p-7"
            >
              <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                Related resources
              </div>
              <div className="mt-4 space-y-3">
                {article.links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`blog-article-related-link-${l.to.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="group flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/40"
                  >
                    <span>
                      <span className="block font-semibold text-slate-900">{l.label}</span>
                      {l.note && (
                        <span className="mt-0.5 block text-base leading-snug text-faint">
                          {l.note}
                        </span>
                      )}
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}
          <p className="mt-10 font-mono text-[13px] uppercase tracking-[0.2em] text-faint">
            Written by Todd Meisler · {article.date}
          </p>
        </div>
      </section>

      <section
        data-testid="blog-article-keep-reading"
        className="border-t border-slate-200 py-16 md:py-20"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Keep reading</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-4xl">
              More guides like this<span className="text-flame">.</span>
            </h2>
          </FadeUp>
          <div className="mt-10">
            <BucketGrid articles={relatedArticles(article, 3)} />
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <div
              data-testid="blog-article-cta-card"
              className="glass relative overflow-hidden rounded-3xl border border-slate-200 p-8 text-center md:p-12"
            >
              <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-flame/15 blur-[90px]" />
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
                Want this applied to your business<span className="text-flame">?</span>
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-mist">
                Book a free 30-minute call with Todd — a plain-language read on
                your website, SEO and advertising.
              </p>
              <button
                data-testid="blog-article-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free call with Todd
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
